import {use, useMemo} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {
  EstablishedAhpSessionContext,
  EstablishedAhpSessionSnapshotContext,
} from '@github-ui/copilot-shared-ui/contexts/EstablishedAhpSessionContext'
import {useChangesetDiffContent} from './use-changeset-diff-content'
import {SavedTaskChangesetContext, useTaskChangeset, type UseTaskChangesetResult} from './use-task-changeset'
import {changesetFilesToDiffData} from '../utils/changeset-to-diff-data'
import type {DiffData} from '../types/diff'

export interface UseDiffSourceResult {
  readonly changeset: UseTaskChangesetResult
  readonly hasChangesetDiffs: boolean
  readonly changesetFilesReady: boolean
  readonly diffs: DiffData | undefined
  readonly isLoading: boolean
  readonly contentUnavailable: boolean
}

/** Loads a session's changeset into `DiffData`, shared by `DiffOverlay` and `DiffWorkbenchContent`. */
export function useDiffSource(enabled: boolean, options: {hideWhitespace?: boolean} = {}): UseDiffSourceResult {
  const {hideWhitespace} = options
  const changesetRolloutEnabled = isFeatureEnabled('copilot_mission_control_ahp_diff') && enabled
  const changeset = useTaskChangeset(changesetRolloutEnabled)
  const changesetFilesReady = changeset.persisted || changeset.status === 'ready' || changeset.status === 'recomputing'
  // Recomputing retains the last completed files; saved files remain visible without live readiness.

  const session = use(EstablishedAhpSessionContext)
  const snapshot = use(EstablishedAhpSessionSnapshotContext)
  const saved = use(SavedTaskChangesetContext)
  const matchingSaved = !session || session.identity.sessionId === saved?.sessionId ? saved : undefined
  const workingDirectories =
    snapshot?.sessionState?.workingDirectories ?? matchingSaved?.bundle.sessionState.workingDirectories
  // Counts-only pass (no content) just to decide whether the changeset actually has displayable
  // files — `changeset.files.length` alone over-counts working-directory-root/directory entries
  // that `changesetFilesToDiffData` filters out, which used to wrongly disable the REST fallback.
  const changesetFileCount = useMemo(
    () => changesetFilesToDiffData(changeset.files, {workingDirectories}).files.length,
    [changeset.files, workingDirectories],
  )
  const hasChangesetDiffs = changeset.available && changesetFilesReady && changesetFileCount > 0

  const diffContent = useChangesetDiffContent(
    changeset.persisted ? undefined : session?.view,
    changeset.files,
    hasChangesetDiffs && changesetRolloutEnabled,
  )
  const changesetContentLoading = hasChangesetDiffs && diffContent.status === 'loading'
  const changesetDiffs = useMemo(
    () =>
      changesetFilesToDiffData(changeset.files, {
        workingDirectories,
        contentByFileId: diffContent.contentByFileId,
        hideWhitespace,
      }),
    [changeset.files, workingDirectories, diffContent.contentByFileId, hideWhitespace],
  )
  // Keep diffs undefined until content settles, so the header doesn't prematurely say "0 files".
  const diffs = hasChangesetDiffs ? (changesetContentLoading ? undefined : changesetDiffs) : undefined

  return {
    changeset,
    hasChangesetDiffs,
    changesetFilesReady,
    diffs,
    isLoading: changesetContentLoading,
    contentUnavailable: hasChangesetDiffs && diffContent.status === 'unavailable',
  }
}
